import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Maximize2, Info, Play, ImageIcon, ArrowRight, ArrowLeft } from "lucide-react";
import moment from "moment";
import { toast } from "react-toastify";
import Tooltip from "@mui/material/Tooltip";
import axios from "axios";
import useTitle from "../Hooks/useTitle";
import notfound from "../assets/images/no-result.png";
import { useUrlParams } from "../submit-review/VideoTestimonials";

const BASE_URL = "https://api.happiclips.com/";

const SkeletonCard = () => (
  <div className="relative bg-white rounded-2xl overflow-hidden shadow-lg animate-pulse">
    <div className="w-full h-[300px] bg-gray-200" />
    <div className="absolute top-3 left-3 bg-gray-300 h-6 w-20 rounded-full" />
    <div className="absolute bottom-0 left-0 right-0 bg-gray-300 h-12" />
  </div>
);

const MediaCard = ({ item, index, setPreviewIndex, handleVideoPlay }) => (
  <div className="relative group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300">
    <div className="relative w-full h-[300px]">
      {item.media_type === "photo" ? (
        <img
          src={`${BASE_URL}${item.media_path}`}
          alt="Media"
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105 cursor-pointer"
          onClick={() => setPreviewIndex(index)}
        />
      ) : (
        <div className="relative w-full h-full">
          <video
            src={`${BASE_URL}${item.media_path}`}
            className="w-full h-full object-cover"
            controls
            onClick={(e) => e.stopPropagation()}
            onPlay={() => handleVideoPlay(index)}
          />
        </div>
      )}
      <div className="absolute top-3 left-3 bg-black bg-opacity-70 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
        {item.media_type === "photo" ? <ImageIcon size={14} /> : <Play size={14} />}
        {item.media_type.toUpperCase()}
      </div>
      <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-all duration-300 flex gap-2">
        <button
          onClick={(e) => {
            e.stopPropagation();
            setPreviewIndex(index);
          }}
          className="bg-white p-2 rounded-full shadow-lg hover:bg-gray-100 transition-colors"
        >
          <Maximize2 size={16} />
        </button>
      </div>
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3 text-white">
        <Tooltip title={moment(item.updated_at).format("MMM DD, YYYY hh:mm A")} arrow placement="top">
          <button className="flex items-center gap-1 text-xs hover:text-blue-200 transition-colors">
            <Info size={14} />
            <span>{moment(item.updated_at).fromNow()}</span>
          </button>
        </Tooltip>
      </div>
    </div>
  </div>
);

const Gallery = () => {
  useTitle("Media Gallery");
  const [activeFilter, setActiveFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [mediaList, setMediaList] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [previewIndex, setPreviewIndex] = useState(null);
  const [playingVideo, setPlayingVideo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false); // New state for preview loading
  const [isPreviewVideoReady, setIsPreviewVideoReady] = useState(false);
  const previewVideoRef = useRef(null);
  const { eventId } = useUrlParams();
  const { t } = useTranslation("media_gallery");
  const isFetchingRef = useRef(false);

  const filteredItems = mediaList?.filter(
    (item) => activeFilter === "all" || item.media_type === activeFilter
  );

  // API functions
  const getEventMedia = async (page = 1) => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}api/event/media/${eventId}?page=${page}&per_page=20`);
      const { data, last_page } = response.data.data;
      setMediaList((prev) => (page === 1 ? data : [...prev, ...data]));
      setTotalPages(last_page);
      setHasMore(page < last_page);
      return data;
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to fetch media");
    } finally {
      setLoading(false);
      setPreviewLoading(false);
    }
  };

  useEffect(() => {
    getEventMedia(1);
  }, [eventId]);

  // Handle preview navigation with pagination
  const handlePreviewNavigation = async (direction) => {
    if (previewLoading || isFetchingRef.current) return;

    const isNext = direction === "next";
    let newIndex = isNext ? previewIndex + 1 : previewIndex - 1;

    if (isNext && newIndex >= filteredItems.length) {
      if (hasMore && !isFetchingRef.current) {
        try {
          isFetchingRef.current = true;
          setPreviewLoading(true);

          const nextPage = currentPage + 1;
          setCurrentPage(nextPage);
          const newData = await getEventMedia(nextPage);

          const addedFiltered = (newData || []).filter(
            (item) => activeFilter === "all" || item.media_type === activeFilter
          );

          if (addedFiltered.length > 0) {
            // Jump to the first newly added item in the filtered list
            setIsPreviewVideoReady(false);
            setPreviewIndex(filteredItems.length);
          } else {
            // If no matching items were added, wrap only if we're at the last page
            if (!hasMore || nextPage >= totalPages) {
              setIsPreviewVideoReady(false);
              setPreviewIndex(0);
            } else {
              // Keep at the last item; user can try next again to fetch further pages
              setIsPreviewVideoReady(false);
              setPreviewIndex(filteredItems.length - 1);
            }
          }
        } catch (error) {
          console.error("Failed to fetch more media:", error);
        } finally {
          setPreviewLoading(false);
          isFetchingRef.current = false;
        }
        return;
      } else {
        // No more pages → loop back to start
        newIndex = 0;
      }
    }

    // If previous goes negative → wrap to last item
    if (!isNext && newIndex < 0) {
      newIndex = filteredItems.length - 1;
    }

    setIsPreviewVideoReady(false);
    setPreviewIndex(newIndex);
  };


  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setPreviewIndex(null);
      if (e.key === "ArrowRight" && previewIndex !== null) handlePreviewNavigation("next");
      if (e.key === "ArrowLeft" && previewIndex !== null) handlePreviewNavigation("prev");
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [previewIndex, filteredItems.length, hasMore, currentPage]);

  const handleLoadMore = () => {
    if (hasMore && !loading) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      getEventMedia(nextPage);
    }
  };

  const handleVideoPlay = (index) => {
    if (playingVideo !== null && playingVideo !== index) {
      const videoElements = document.querySelectorAll("video");
      if (videoElements[playingVideo]) videoElements[playingVideo].pause();
    }
    setPlayingVideo(index);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-12">
        <div className="mx-auto">
          {/* Header */}
          <header className="mb-4 flex flex-col md:flex-row justify-between items-center gap-4">
            <div>
              <h1 className="text-3xl font-semibold text-gray-900">
                {t("title_media_gallery")}
              </h1>
              <p className="text-gray-600">{t("manage_image_n_video")}</p>
            </div>
            <div className="flex justify-between items-center flex-wrap gap-4">
              <div className="flex gap-2 bg-white p-1 rounded-lg shadow-sm">
                {["all", "photo", "video"].map((filter) => (
                  <button
                    key={filter}
                    onClick={() => {
                      setActiveFilter(filter);
                      setPreviewIndex(null); // Close preview when filter changes
                    }}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 capitalize ${activeFilter === filter
                      ? "bg-blue-500 text-white shadow-sm"
                      : "text-gray-600 hover:text-gray-800 hover:bg-gray-100"
                      }`}
                  >
                    {filter === "all" ? t("all") : t(filter) + "s"}
                  </button>
                ))}
              </div>
            </div>
          </header>

          {/* Media Grid */}
          {loading && !mediaList.length ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {[...Array(8)].map((_, i) => <SkeletonCard key={i} />)}
            </div>
          ) : filteredItems.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {filteredItems.map((item, index) => (
                  <MediaCard
                    key={item.id}
                    item={item}
                    index={index}
                    setPreviewIndex={setPreviewIndex}
                    handleVideoPlay={handleVideoPlay}
                  />
                ))}
              </div>
              {hasMore && (
                <div className="flex justify-center mt-6">
                  <button
                    onClick={handleLoadMore}
                    className="px-6 py-2 text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition"
                    disabled={loading}
                  >
                    {loading ? t("loading") : t("load_more")}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col justify-center items-center text-center h-[calc(100vh-300px)] w-full">
              <img
                src={notfound}
                alt="No Media"
                className="w-40 h-40 sm:w-60 sm:h-60"
              />
              <p className="text-lg font-medium mt-4">{t("no_media_found")}</p>
            </div>
          )}

          {/* Full-Screen Preview */}
          {previewIndex !== null && (
            <div className="fixed inset-0 z-50 bg-black bg-opacity-95 flex items-center justify-center">
              <button
                onClick={() => setPreviewIndex(null)}
                className="absolute top-5 right-5 text-white text-2xl z-50 bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-70 transition-colors"
              >
                ✕
              </button>
              <div className="relative w-full h-full flex items-center justify-center">
                {filteredItems[previewIndex].media_type === "photo" ? (
                  <img
                    src={`${BASE_URL}${filteredItems[previewIndex].media_path}`}
                    alt="Preview"
                    className="max-w-full max-h-full object-contain"
                  />
                ) : (
                  <video
                    key={filteredItems[previewIndex].id}
                    ref={previewVideoRef}
                    src={`${BASE_URL}${filteredItems[previewIndex].media_path}`}
                    className="max-w-full max-h-full"
                    controls
                    autoPlay
                    muted
                    playsInline
                    preload="auto"
                    onCanPlay={() => {
                      setIsPreviewVideoReady(true);
                      try { previewVideoRef.current && previewVideoRef.current.play(); } catch (e) { /* ignore */ }
                    }}
                    onLoadedData={() => setIsPreviewVideoReady(true)}
                    onError={() => toast.error("Failed to load video")}
                  />
                )}
                {(previewLoading || (filteredItems[previewIndex].media_type === "video" && !isPreviewVideoReady)) && (
                  <div className="absolute inset-0 flex items-center justify-center text-white text-lg bg-black/20">Loading...</div>
                )}
                <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-70 text-white px-4 py-2 rounded-full text-sm">
                  {previewIndex + 1} of {filteredItems.length}
                  {hasMore && " (more available)"}
                </div>
              </div>
              {filteredItems.length > 1 && (
                <>
                  <button
                    className="absolute left-6 top-1/2 transform -translate-y-1/2 text-white text-3xl bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-70 transition-colors"
                    onClick={() => handlePreviewNavigation("prev")}
                    disabled={previewLoading}
                  >
                    <ArrowLeft size={20} />
                  </button>
                  <button
                    className="absolute right-6 top-1/2 transform -translate-y-1/2 text-white text-3xl bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-70 transition-colors"
                    onClick={() => handlePreviewNavigation("next")}
                    disabled={previewLoading}
                  >
                    <ArrowRight size={20} />
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Gallery;